import { eyepieceRing } from '../spores/focus';

/*
 * Which frames of a Z-stack are worth keeping (docs/FIELD_AI_VISION_SPEC.md
 * 10.3, phase A5). Pure, no DOM: the camera hands over each frame's grey
 * levels, this scores them and chooses.
 *
 *   tileSharpness()  the focus meter's sharpness (utils/spores/focus.js: the
 *                    mean squared Laplacian after 2 x 2 binning), per cell of
 *                    a GRID x GRID grid, so a frame sharp in one corner only
 *                    still counts for that corner. At the microscope the
 *                    eyepiece's dark ring is left out (its edge is sharp
 *                    whatever the focus).
 *   select()         drops the completely blurred (under BLURRED of the
 *                    sharpest frame); keeps every frame that is the sharpest
 *                    for at least one tile -- it holds a plane no other frame
 *                    has -- and every frame at KEEP of the sharpest or more;
 *                    keeps only the sharper of two neighbours that are near
 *                    duplicates (tile profiles correlating above
 *                    DUPLICATE_CORRELATION, sharpness within
 *                    DUPLICATE_SHARPNESS); keeps at most MAX_LAYERS, the tile
 *                    winners first; and orders what it keeps by focus distance
 *                    (focus stepping) or by time (a hand sweep, which may
 *                    reverse).
 */
export const GRID = 4;
export const BLURRED = 0.25;
export const KEEP = 0.6;
export const DUPLICATE_CORRELATION = 0.98;
export const DUPLICATE_SHARPNESS = 0.03;
export const MAX_LAYERS = 24;

/** The mean squared Laplacian of each cell of a grid x grid grid, row by row (0 for a cell with nothing to read). */
export function tileSharpness(gray, w, h, { ring = false, grid = GRID } = {}) {
  const bw = Math.floor(w / 2);
  const bh = Math.floor(h / 2);
  const tiles = new Array(grid * grid).fill(0);
  if (bw < 3 || bh < 3) return tiles;
  const binned = new Float32Array(bw * bh);
  for (let y = 0; y < bh; y += 1) {
    for (let x = 0; x < bw; x += 1) {
      const i = 2 * y * w + 2 * x;
      binned[y * bw + x] = (gray[i] + gray[i + 1] + gray[i + w] + gray[i + w + 1]) / 4;
    }
  }
  const out = ring ? eyepieceRing(binned, bw, bh) : null;
  const sums = new Float64Array(grid * grid);
  const counts = new Uint32Array(grid * grid);
  for (let y = 1; y < bh - 1; y += 1) {
    const ty = Math.min(grid - 1, Math.floor((y * grid) / bh));
    for (let x = 1; x < bw - 1; x += 1) {
      const i = y * bw + x;
      if (out && out[i]) continue;
      const lap = binned[i - 1] + binned[i + 1] + binned[i - bw] + binned[i + bw] - 4 * binned[i];
      const t = ty * grid + Math.min(grid - 1, Math.floor((x * grid) / bw));
      sums[t] += lap * lap;
      counts[t] += 1;
    }
  }
  for (let t = 0; t < tiles.length; t += 1) tiles[t] = counts[t] ? sums[t] / counts[t] : 0;
  return tiles;
}

/** A frame's sharpness: the mean of its tiles. */
export function frameSharpness(tiles) {
  if (!tiles?.length) return 0;
  return tiles.reduce((a, b) => a + b, 0) / tiles.length;
}

/** Pearson's correlation of two tile profiles; two flat profiles correlate when they are equal. */
export function correlation(a, b) {
  const n = Math.min(a.length, b.length);
  if (!n) return 0;
  let ma = 0;
  let mb = 0;
  for (let i = 0; i < n; i += 1) { ma += a[i]; mb += b[i]; }
  ma /= n;
  mb /= n;
  let cov = 0;
  let va = 0;
  let vb = 0;
  for (let i = 0; i < n; i += 1) {
    cov += (a[i] - ma) * (b[i] - mb);
    va += (a[i] - ma) ** 2;
    vb += (b[i] - mb) ** 2;
  }
  if (!va || !vb) return va === vb && ma === mb ? 1 : 0;
  return cov / Math.sqrt(va * vb);
}

const byOrder = order => (a, b) => {
  if (order === 'focus_distance') {
    const da = Number.isFinite(a.focus_distance) ? a.focus_distance : Infinity;
    const db = Number.isFinite(b.focus_distance) ? b.focus_distance : Infinity;
    if (da !== db) return da - db;
  }
  return (a.t_ms ?? 0) - (b.t_ms ?? 0) || a.index - b.index;
};

/**
 * Chooses a stack's layers from `frames` -- [{tiles, focus_distance?, t_ms}],
 * in the order taken -- for `order` 'focus_distance' or 'time'.
 *
 * -> {layers: [{index, sharpness, tile_wins, kept: 'tile_winner'|'sharp'}] in
 *    that order, cover: the position of the sharpest layer, rejected:
 *    {blurred, weak, duplicate, over_cap}} -- `index` is the frame's place in
 *    `frames`; `weak` counts the frames between BLURRED and KEEP that win no
 *    tile.
 */
export function select(frames, { order = 'focus_distance', maxLayers = MAX_LAYERS } = {}) {
  const rejected = { blurred: 0, weak: 0, duplicate: 0, over_cap: 0 };
  const scored = (frames || []).map((f, index) => ({
    index, tiles: f.tiles || [], sharpness: frameSharpness(f.tiles), focus_distance: f.focus_distance, t_ms: f.t_ms,
  }));
  const best = Math.max(0, ...scored.map(f => f.sharpness));
  if (!(best > 0)) return { layers: [], cover: null, rejected: { ...rejected, blurred: scored.length } };

  const alive = scored.filter((f) => {
    if (f.sharpness < BLURRED * best) { rejected.blurred += 1; return false; }
    return true;
  });
  // The sharpest frame of each tile, among those not blurred.
  for (const f of alive) f.tile_wins = 0;
  const tileCount = Math.max(0, ...alive.map(f => f.tiles.length));
  for (let t = 0; t < tileCount; t += 1) {
    let winner = null;
    for (const f of alive) if ((f.tiles[t] || 0) > 0 && (!winner || f.tiles[t] > winner.tiles[t])) winner = f;
    if (winner) winner.tile_wins += 1;
  }
  let kept = alive.filter((f) => {
    if (f.tile_wins > 0 || f.sharpness >= KEEP * best) return true;
    rejected.weak += 1;
    return false;
  });

  // Near-duplicates: of two neighbours that show the same thing, the sharper.
  kept.sort(byOrder(order));
  const distinct = [];
  for (const f of kept) {
    const last = distinct[distinct.length - 1];
    const same = last && correlation(last.tiles, f.tiles) > DUPLICATE_CORRELATION
      && Math.abs(last.sharpness - f.sharpness) < DUPLICATE_SHARPNESS * Math.max(last.sharpness, f.sharpness);
    if (!same) distinct.push(f);
    else {
      rejected.duplicate += 1;
      if (f.sharpness > last.sharpness) distinct[distinct.length - 1] = f;
    }
  }
  kept = distinct;

  if (kept.length > maxLayers) {
    const ranked = [...kept].sort((a, b) => (b.tile_wins > 0) - (a.tile_wins > 0) || b.tile_wins - a.tile_wins
      || b.sharpness - a.sharpness);
    const chosen = new Set(ranked.slice(0, maxLayers));
    rejected.over_cap = kept.length - maxLayers;
    kept = kept.filter(f => chosen.has(f));
  }

  const layers = kept.map(f => ({
    index: f.index, sharpness: f.sharpness, tile_wins: f.tile_wins, kept: f.tile_wins > 0 ? 'tile_winner' : 'sharp',
  }));
  let cover = null;
  layers.forEach((l, i) => { if (cover === null || l.sharpness > layers[cover].sharpness) cover = i; });
  return { layers, cover, rejected };
}
